import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Send, CheckCircle2, ChevronRight, X } from 'lucide-react';
import {
  enviarAltaGruposLote,
  getLegajosListosCounts,
  getUltimasAltasGrupos,
  listarLegajosPorCategoria,
  type CategoriaCount,
  type CountsPorEstado,
  type LegajoCategoria,
  type LegajoEstadoVista,
  type LegajosListosCounts,
} from '@lib/services/altas-masivas.service';
import { EnvioLoteModal, ModalOverlay } from '@components/EnvioLoteModal';
import { useRefrescarPipeline } from '@lib/hooks/useRefrescarPipeline';
import { TableSkeleton } from '@components/medicus-ui/Loaders';
import { UltimasAltas, EstadoBinary } from '@components/UltimasAltas';
import { fechaHora } from '@lib/utils/fecha';
import { capitalizeName } from '@lib/utils/text';
import { useAuthStore } from '@lib/store/auth.store';
import { UserRole, isMedicus } from '@lib/types';
import { puedeVer as tieneVista } from '@lib/permissions/catalog';
import { useCanMutate } from '@lib/hooks/useCanMutate';
import { estadoLegajoInfo } from '@lib/estado-legajo';

/**
 * Vista "Legajos listos para alta": el pipeline de la alta masiva.
 *
 * Cada legajo recorre 2 etapas:
 *   Pendientes de Alta  →  Dados de alta
 *
 * Arriba mostramos el embudo (totales por etapa) y abajo el desglose por
 * categoría (OK / Observaciones / Preexistencias) con la acción de envío
 * que da de alta los pendientes de una categoría en Binary por API.
 *
 * Las etapas se mapean a estados de legajo en el backend:
 *   pendientes → validado_por_titular | dadoDeAlta → dado_de_alta
 *
 * `en_alta_binary` ya NO se muestra: era el limbo del circuito viejo (exportar
 * a Excel y subirlo a mano). Con el alta por API el legajo pasa directo de
 * `validado_por_titular` a `dado_de_alta` (o `rechazado_binary`), así que la
 * etapa quedaba siempre en cero. El backend sigue devolviendo el contador.
 */
type StageKey = 'pendientes' | 'dadoDeAlta';

interface StageDef {
  key: StageKey;
  estadoVista: LegajoEstadoVista;
  titulo: string;
  hint: string;
  color: string;
  bg: string;
  Icon: typeof Clock;
}

// Colores espejados del funnel del Dashboard (dado_de_alta verde) + ámbar
// para "pendientes" (acción requerida).
const STAGES: StageDef[] = [
  {
    key: 'pendientes',
    estadoVista: 'pendientes',
    titulo: 'Pendientes de Alta',
    hint: 'Completaron el wizard. Listos para enviar a Binary.',
    color: '#B45309',
    bg: 'rgba(180, 83, 9, 0.08)',
    Icon: Clock,
  },
  {
    key: 'dadoDeAlta',
    estadoVista: 'dado_de_alta',
    titulo: 'Dados de alta',
    hint: 'Alta confirmada. Cierra el circuito de admisión.',
    color: '#166534',
    bg: 'rgba(22, 101, 52, 0.10)',
    Icon: CheckCircle2,
  },
];

interface CategoriaConfig {
  key: LegajoCategoria;
  titulo: string;
  descripcion: string;
}

const CATEGORIAS: CategoriaConfig[] = [
  { key: 'ok', titulo: 'Legajos OK', descripcion: 'Sin observaciones ni preexistencias declaradas.' },
  {
    key: 'observaciones',
    titulo: 'Con observaciones',
    descripcion: 'Tienen observaciones pendientes y no declararon preexistencias.',
  },
  { key: 'preexistencias', titulo: 'Con preexistencias', descripcion: 'Declararon al menos una preexistencia en la DDJJ.' },
];

const VACIO: CategoriaCount = { legajos: 0, personas: 0 };
const COUNTS_VACIO: CountsPorEstado = {
  ok: VACIO,
  observaciones: VACIO,
  preexistencias: VACIO,
  frenados: VACIO,
};

function fmt(n: number): string {
  return n.toLocaleString('es-AR');
}

/**
 * Devuelve los conteos de una etapa de forma segura. `dadoDeAlta` es nuevo en
 * el backend: si la UI se despliega antes que la API, el campo viene undefined
 * y caemos a ceros en vez de romper.
 */
function etapaCounts(counts: LegajosListosCounts, key: StageKey): CountsPorEstado {
  return counts[key] ?? COUNTS_VACIO;
}

/** Suma las 3 categorías de una etapa para el total del embudo. */
function totalEtapa(c: CountsPorEstado | undefined): CategoriaCount {
  if (!c) return VACIO;
  return {
    legajos: c.ok.legajos + c.observaciones.legajos + c.preexistencias.legajos,
    personas: c.ok.personas + c.observaciones.personas + c.preexistencias.personas,
  };
}

function labelCategoria(c: LegajoCategoria): string {
  return CATEGORIAS.find((cat) => cat.key === c)?.titulo ?? c;
}

interface DetalleSelection {
  categoria: LegajoCategoria;
  stage: StageDef;
}

interface EnvioPedido {
  categoria: Extract<LegajoCategoria, 'ok' | 'preexistencias'>;
  cantidad: number;
}

export function LegajosListosPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [detalle, setDetalle] = useState<DetalleSelection | null>(null);
  const [envio, setEnvio] = useState<EnvioPedido | null>(null);

  // Gate por PERMISO DE VISTA, no por rol. Con `isMedicus(rol)` la organización
  // veía "Alta Grupos" en el menú (el ítem se pinta con el permiso efectivo, que
  // incluye los overrides) y al entrar le salía "No tenés permisos" — el bug
  // reportado el 31/08. El backend ahora gatea con la misma clave.
  const puedeVer = user ? tieneVista(user, 'view:legajos-listos') : false;
  // Mirar no es despachar: la organización ve su cola pero no manda a Binary
  // (los POST siguen exigiendo ADMIN/SUPERADMIN). Sin este corte le mostraríamos
  // botones que el server rechaza con 403.
  const puedeMutar = useCanMutate() && isMedicus(user?.rol ?? UserRole.USUARIO_EMPRESA);

  const countsQ = useQuery({
    queryKey: ['legajos-listos-counts'],
    queryFn: getLegajosListosCounts,
    // 60s: cada poll recorre ~9k legajos + sus DDJJs en Mongo. Alcanza con un
    // refresh tranquilo, no hace falta tiempo real.
    refetchInterval: 60_000,
    enabled: puedeVer,
  });

  function invalidarPipeline() {
    queryClient.invalidateQueries({ queryKey: ['legajos-listos-counts'] });
    queryClient.invalidateQueries({ queryKey: ['legajos-listos-detalle'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-global'] });
  }

  // Al TERMINAR el job se hace un refetch DEFINITIVO en vez de invalidar:
  // durante el envío puede haber un fetch de contadores en vuelo (arrancado en
  // un tick intermedio) que captura un estado parcial; `invalidateQueries` se
  // dedup con ese fetch en curso y no lo reemplaza → la lista quedaba mostrando
  // "pendiente de alta" tras cerrar el modal.
  const refrescarPipeline = useRefrescarPipeline();

  if (!puedeVer) {
    return (
      <section>
        <h1 className="heading-3">Alta Grupos</h1>
        <p className="text-element-mid">No tenés permisos para ver esta vista.</p>
      </section>
    );
  }

  const counts = countsQ.data;

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Alta Grupos</h1>
        <p className="text-element-mid">
          Pipeline de la admisión masiva: de <strong>pendientes</strong> a <strong>dados de alta</strong>. Al
          enviar una categoría, cada legajo (titular + familiares) se da de alta en Binary por API; los que
          transforman pasan a “{estadoLegajoInfo('dado_de_alta').label}” y los que rebotan a “{estadoLegajoInfo('rechazado_binary').label}”.
        </p>
      </header>

      {/* Banda fina cuando refresca en background con data ya visible. */}
      {counts && countsQ.isFetching && <div className="refetchBar" style={{ marginTop: 16 }} />}

      {countsQ.isLoading && <PipelineSkeleton />}
      {countsQ.isError && (
        <p className="status status-error" style={{ marginTop: 24, padding: '10px 14px' }}>
          No se pudieron cargar los contadores. Probá refrescar la página.
        </p>
      )}

      {counts && (
        <>
          <PipelineOverview counts={counts} />

          <h2 className="heading-6" style={{ marginTop: 32, marginBottom: 4 }}>
            Desglose por categoría
          </h2>
          <p className="text-element-mid" style={{ fontSize: 13, marginBottom: 16 }}>
            Cada legajo cae en una sola categoría — prioridad observaciones › preexistencias › OK. El envío solo
            aplica a los <strong>pendientes</strong>; los legajos <strong>con observaciones</strong> quedan retenidos
            hasta resolverlas.
          </p>

          {counts.pendientes.frenados.legajos > 0 && (
            <p
              className="status status-warning"
              style={{ display: 'block', fontSize: 13, marginBottom: 16, padding: '10px 14px' }}
            >
              ⚠ {fmt(counts.pendientes.frenados.legajos)} legajos ({fmt(counts.pendientes.frenados.personas)}{' '}
              personas) quedan <strong>frenados por titular bloqueado</strong> — no se exportan y por eso no
              cuentan en las categorías de abajo. Aparecen en la sección <strong>Bloqueados</strong>.
            </p>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 16,
            }}
          >
            {CATEGORIAS.map((cat) => (
              <CategoriaCard
                key={cat.key}
                cat={cat}
                counts={counts}
                puedeMutar={puedeMutar}
                onOpenDetalle={(stage) => setDetalle({ categoria: cat.key, stage })}
                onEnviar={(cantidad) =>
                  cat.key !== 'observaciones' &&
                  setEnvio({ categoria: cat.key, cantidad })
                }
              />
            ))}
          </div>
        </>
      )}

      <UltimasAltas
        queryKey="ultimas-altas-grupos"
        fetcher={getUltimasAltasGrupos}
        columnas={['Fecha y hora', 'Legajo', 'Titular', 'DNI', 'Personas', 'MEDICARD', 'Estado', 'Plan actual']}
        claveFila={(a) => a.legajoId}
        fila={(a) => [
          fechaHora(a.altaAt),
          a.numero,
          capitalizeName(a.titular),
          a.documento,
          a.personas,
          a.medicard ?? '—',
          <EstadoBinary estado={a.estadoBinary} />,
          a.planActual ?? '—',
        ]}
        vacio="Todavía no se envió ningún grupo a Binary desde esta pantalla."
      />

      {detalle && (
        <DetalleDrawer categoria={detalle.categoria} stage={detalle.stage} onClose={() => setDetalle(null)} />
      )}

      {envio && (
        <EnvioLoteModal
          titulo={`Enviar a Binary · ${labelCategoria(envio.categoria)}`}
          cantidad={envio.cantidad}
          confirmacion={
            <p style={{ margin: 0 }}>
              Vas a dar de alta en Binary <strong>{fmt(envio.cantidad)}</strong> legajos de{' '}
              <strong>{labelCategoria(envio.categoria)}</strong> por API. Cada grupo (titular +
              familiares) se envía como una solicitud; los que transforman quedan{' '}
              <strong>dados de alta</strong> y los que rebotan, <strong>rechazados</strong> con el
              motivo.
            </p>
          }
          iniciar={() => enviarAltaGruposLote({ categoria: envio.categoria })}
          onCerrar={() => setEnvio(null)}
          onProgreso={invalidarPipeline}
          onFin={refrescarPipeline}
        />
      )}
    </section>
  );
}

/* ─────────────────────────── Embudo (totales por etapa) ─────────────────── */

function PipelineOverview({ counts }: { counts: LegajosListosCounts }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 16,
        marginTop: 20,
      }}
    >
      {STAGES.map((s, i) => {
        const t = totalEtapa(etapaCounts(counts, s.key));
        return (
          <article
            key={s.key}
            className="card"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              borderTop: `3px solid ${s.color}`,
              position: 'relative',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  display: 'inline-flex',
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: s.bg,
                  color: s.color,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <s.Icon size={21} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: 'var(--color-element-low)' }}>
                  Etapa {i + 1}
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-element-high)' }}>{s.titulo}</div>
              </div>
            </div>

            <div>
              <span
                style={{
                  fontFamily: 'var(--font-switzer)',
                  fontSize: 36,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: s.color,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmt(t.legajos)}
              </span>
              <span className="text-element-mid" style={{ fontSize: 13, marginLeft: 6 }}>
                legajos
              </span>
              <div className="text-element-mid" style={{ fontSize: 12, marginTop: 4 }}>
                {fmt(t.personas)} personas
              </div>
            </div>

            <p className="text-element-mid" style={{ fontSize: 12, marginTop: 'auto', lineHeight: 1.45 }}>
              {s.hint}
            </p>
          </article>
        );
      })}
    </div>
  );
}

/* ─────────────────────────── Card por categoría ─────────────────────────── */

function CategoriaCard(props: {
  cat: CategoriaConfig;
  counts: LegajosListosCounts;
  puedeMutar: boolean;
  onOpenDetalle: (stage: StageDef) => void;
  onEnviar: (cantidad: number) => void;
}) {
  const { cat, counts, puedeMutar, onOpenDetalle, onEnviar } = props;
  const pendientes = etapaCounts(counts, 'pendientes')[cat.key];
  const sinPendientes = pendientes.legajos === 0;
  // Los legajos con observaciones pendientes no se envían por API: quedan
  // retenidos hasta que el admin resuelva las observaciones.
  const esObservaciones = cat.key === 'observaciones';
  const deshabilitado = sinPendientes || esObservaciones;
  const tituloBoton = esObservaciones
    ? 'Los legajos con observaciones no se envían hasta resolverlas.'
    : sinPendientes
      ? 'No hay legajos pendientes para enviar en esta categoría.'
      : `Enviar a Binary por API ${fmt(pendientes.legajos)} legajos (${fmt(pendientes.personas)} personas).`;

  return (
    <article className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <header>
        <h3 className="heading-6" style={{ margin: 0, fontSize: 17 }}>
          {cat.titulo}
        </h3>
        <p className="text-element-mid" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.45 }}>
          {cat.descripcion}
        </p>
      </header>

      <div style={{ display: 'grid', gap: 6 }}>
        {STAGES.map((s) => {
          const c = etapaCounts(counts, s.key)[cat.key];
          return (
            <StageRow
              key={s.key}
              stage={s}
              count={c}
              onClick={c.legajos > 0 ? () => onOpenDetalle(s) : undefined}
            />
          );
        })}
      </div>

      {puedeMutar && (
        <button
          type="button"
          className="btn btn-primary"
          disabled={deshabilitado}
          onClick={() => onEnviar(pendientes.legajos)}
          style={{ marginTop: 'auto', width: '100%' }}
          title={tituloBoton}
        >
          <Send size={16} />
          {esObservaciones
            ? 'Retenidos (observaciones)'
            : sinPendientes
              ? 'Sin pendientes'
              : `Enviar ${fmt(pendientes.legajos)} a Binary`}
        </button>
      )}
    </article>
  );
}

function StageRow(props: { stage: StageDef; count: CategoriaCount; onClick?: () => void }) {
  const { stage, count, onClick } = props;
  const clickable = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      title={clickable ? 'Ver detalle' : 'Sin legajos en esta etapa'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 11px',
        background: clickable ? stage.bg : 'transparent',
        border: `1px solid ${clickable ? 'transparent' : 'var(--color-outline)'}`,
        borderRadius: 8,
        cursor: clickable ? 'pointer' : 'default',
        textAlign: 'left',
        color: 'inherit',
        width: '100%',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          width: 28,
          height: 28,
          borderRadius: 8,
          background: clickable ? 'rgba(255,255,255,0.7)' : 'var(--color-surface-accent)',
          color: count.legajos > 0 ? stage.color : 'var(--color-element-low)',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <stage.Icon size={15} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--color-element-high)' }}>
          {stage.titulo}
        </span>
        <span className="text-element-mid" style={{ fontSize: 11 }}>
          {fmt(count.personas)} personas
        </span>
      </span>
      <strong
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: 18,
          color: count.legajos > 0 ? stage.color : 'var(--color-element-low)',
        }}
      >
        {fmt(count.legajos)}
      </strong>
      {clickable && <ChevronRight size={15} style={{ color: 'var(--color-element-low)', flexShrink: 0 }} />}
    </button>
  );
}

/* ─────────────────────────── Skeleton de carga ──────────────────────────── */

function PipelineSkeleton() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 16,
        marginTop: 20,
      }}
    >
      {[0, 1, 2].map((i) => (
        <article key={i} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <span className="skeleton" style={{ width: 40, height: 40, borderRadius: 12 }} />
          <span className="skeleton" style={{ width: '50%', height: 32 }} />
          <span className="skeleton" style={{ width: '80%', height: 12 }} />
        </article>
      ))}
    </div>
  );
}

/* ─────────────────────────── Drawer de detalle ──────────────────────────── */

function DetalleDrawer(props: { categoria: LegajoCategoria; stage: StageDef; onClose: () => void }) {
  const { categoria, stage, onClose } = props;
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const q = useQuery({
    queryKey: ['legajos-listos-detalle', categoria, stage.estadoVista, page],
    queryFn: () => listarLegajosPorCategoria(categoria, stage.estadoVista, page, pageSize),
    placeholderData: keepPreviousData,
  });

  const totalPages = Math.max(1, Math.ceil((q.data?.total ?? 0) / pageSize));

  return (
    <ModalOverlay onClose={onClose}>
      <div
        className="card"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(960px, 100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <header className="rowBetween" style={{ alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                display: 'inline-flex',
                width: 34,
                height: 34,
                borderRadius: 10,
                background: stage.bg,
                color: stage.color,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <stage.Icon size={18} />
            </span>
            <div>
              <h3 className="heading-6" style={{ margin: 0 }}>
                {labelCategoria(categoria)} · {stage.titulo}
              </h3>
              <p className="text-element-mid" style={{ fontSize: 12, marginTop: 2 }}>
                {fmt(q.data?.total ?? 0)} legajos
              </p>
            </div>
          </div>
          <button type="button" className="modalClose" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </header>

        <div style={{ overflow: 'auto', flex: 1 }}>
          {q.isLoading && <TableSkeleton rows={6} />}
          {q.isError && <p className="status status-error">No se pudo cargar el detalle.</p>}
          {q.data && q.data.items.length === 0 && <p className="text-element-mid">Sin legajos en esta etapa.</p>}
          {q.data && q.data.items.length > 0 && (
            <div className="tableWrap">
              <table className="dataTable">
                <thead>
                  <tr>
                    <th>Legajo</th>
                    <th>Familiares</th>
                    <th>Observaciones</th>
                    <th>Preexistencias</th>
                    <th>Finalizado</th>
                    <th>Padrón</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.items.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <strong>{l.numero}</strong>
                      </td>
                      <td>{l.familiaresAsociadoIds.length}</td>
                      <td>
                        {l.observacionesPendientes > 0 ? (
                          <span className="status status-warning" style={{ fontSize: 12 }}>
                            {l.observacionesPendientes} pendiente{l.observacionesPendientes === 1 ? '' : 's'}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-element-mid)', fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td>
                        {l.tienePreexistencias ? (
                          <span className="status status-info" style={{ fontSize: 12 }}>
                            Sí
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-element-mid)', fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {l.activacionCompletadaAt ? new Date(l.activacionCompletadaAt).toLocaleString('es-AR') : '—'}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{l.padronId.slice(0, 8)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {q.data && q.data.total > pageSize && (
          <footer className="rowBetween" style={{ alignItems: 'center' }}>
            <span className="text-element-mid" style={{ fontSize: 12 }}>
              Página {page} de {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente
              </button>
            </div>
          </footer>
        )}
      </div>
    </ModalOverlay>
  );
}
